import React, { createContext, useState, useEffect, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  getFirestore, doc, getDoc, setDoc, collection, getDocs, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseConfig } from '../firebaseConfig';
import {
  loadAndCacheFavorites,
  toggleFavorite,
  getCachedFavorites,
  setCachedFavorites,
  clearCachedFavorites,
} from '../services/favoritesService';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

const GUEST_LOCATION_KEY = 'ff_guest_location';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [localStorageLoaded, setLocalStorageLoaded] = useState(false);
  const [guestLocation, setGuestLocation] = useState(null);
  const [addresses, setAddresses] = useState([]);
  // Kept as 'users' always now — every account (consumer or business) lives
  // at users/{uid}. Still exposed via context (rather than hardcoded in
  // every page) so downstream reads/writes (purchases, addresses, payment
  // methods) stay decoupled from this decision.
  const [userCollection, setUserCollection] = useState('users');
  const [needsAddress, setNeedsAddress] = useState(false);
  const [favorites, setFavorites] = useState(() => getCachedFavorites());
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const openAddressModal = () => setShowAddressModal(true);
  const closeAddressModal = () => setShowAddressModal(false);
  const openSignupModal = () => setShowSignupModal(true);
  const closeSignupModal = () => setShowSignupModal(false);

  // Load guest location from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(GUEST_LOCATION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.lat && parsed.lng) {
          setGuestLocation(parsed);
        }
      }
    } catch {
      // ignore parse errors
    }
    setLocalStorageLoaded(true);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadUserData(currentUser.uid);
      } else {
        setUser(null);
        setUserData(null);
        setNeedsAddress(false);
      }
      setAuthResolved(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authResolved && localStorageLoaded) {
      setLoading(false);
    }
  }, [authResolved, localStorageLoaded]);

  const loadAddressesForUid = async (uid, collectionName) => {
    try {
      const snap = await getDocs(collection(db, collectionName, uid, 'addresses'));
      const list = [];
      snap.docs.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      return list;
    } catch (e) {
      console.warn(`Failed to load addresses from ${collectionName}:`, e.message);
      return [];
    }
  };

  const ensureStripeCustomer = async (uid, data) => {
    if (data.stripeCustomerId) return data.stripeCustomerId;
    try {
      const createFn = httpsCallable(functions, 'createPlatformStripeCustomer');
      const result = await createFn({
        name: data.name || data.displayName || '',
        email: data.email || '',
        phone: data.phone || '',
      });
      const stripeCustomerId = result.data.stripeCustomerId;
      // Update local userData
      setUserData((prev) => (prev ? { ...prev, stripeCustomerId } : prev));
      return stripeCustomerId;
    } catch (e) {
      console.error('Error ensuring Stripe customer:', e);
      return null;
    }
  };

  // One-time, additive, self-healing migration for accounts created back
  // when public-web kept two separate identities: consumer accounts in
  // `consumers/{uid}` and business/admin accounts in `users/{uid}`. The
  // ecosystem is now unified — every person has exactly one profile at
  // `users/{uid}`, and a consumer who later onboards a restaurant (Stripe
  // onboarding) just gets extra fields/subcollections layered onto that
  // same doc instead of a second account. New signups (SignupModal) now
  // write directly to `users/{uid}`; this only runs for pre-existing
  // accounts that still have a legacy `consumers/{uid}` doc.
  //
  // Deliberately additive/non-destructive: it never deletes or overwrites
  // `users/{uid}` fields that already exist (e.g. a restaurant owner who
  // separately also has a consumer profile keeps their business data
  // untouched), and it leaves the original `consumers/{uid}` doc in place
  // as a safety net — it just stops being read once the merge is done.
  const migrateConsumerToUser = async (uid, existingUserData) => {
    const consumerDoc = await getDoc(doc(db, 'consumers', uid));
    if (!consumerDoc.exists()) return existingUserData;

    const consumerData = consumerDoc.data();
    const patch = { migratedFromConsumers: true, migratedAt: serverTimestamp() };
    if (!existingUserData) {
      // No users/{uid} doc yet: move the whole consumer profile over.
      Object.assign(patch, consumerData);
    } else {
      // users/{uid} already exists — only fill in fields it doesn't have.
      ['name', 'email', 'phone', 'address', 'lat', 'lng', 'consents', 'stripeCustomerId'].forEach((f) => {
        if (existingUserData[f] === undefined && consumerData[f] !== undefined) {
          patch[f] = consumerData[f];
        }
      });
    }
    await setDoc(doc(db, 'users', uid), patch, { merge: true });

    // Copy the addresses subcollection over too, but only if users/{uid}
    // doesn't already have any (avoid duplicating on repeat logins).
    try {
      const userAddrSnap = await getDocs(collection(db, 'users', uid, 'addresses'));
      if (userAddrSnap.empty) {
        const consumerAddrSnap = await getDocs(collection(db, 'consumers', uid, 'addresses'));
        if (!consumerAddrSnap.empty) {
          const docs = consumerAddrSnap.docs;
          for (let i = 0; i < docs.length; i += 400) {
            const batch = writeBatch(db);
            docs.slice(i, i + 400).forEach((d) => {
              batch.set(doc(db, 'users', uid, 'addresses', d.id), d.data());
            });
            await batch.commit();
          }
        }
      }
    } catch (e) {
      console.warn('Error migrating consumer addresses:', e.message);
    }

    // Copy past orders (consumers/{uid}/purchases) too, same guard.
    try {
      const userPurchSnap = await getDocs(collection(db, 'users', uid, 'purchases'));
      if (userPurchSnap.empty) {
        const consumerPurchSnap = await getDocs(collection(db, 'consumers', uid, 'purchases'));
        if (!consumerPurchSnap.empty) {
          const docs = consumerPurchSnap.docs;
          for (let i = 0; i < docs.length; i += 400) {
            const batch = writeBatch(db);
            docs.slice(i, i + 400).forEach((d) => {
              batch.set(doc(db, 'users', uid, 'purchases', d.id), d.data());
            });
            await batch.commit();
          }
        }
      }
    } catch (e) {
      console.warn('Error migrating consumer purchases:', e.message);
    }

    return { ...(existingUserData || {}), ...patch };
  };

  const loadUserData = async (uid) => {
    try {
      // Only overwrite guestLocation if the user hasn't actively selected one.
      // An active guestLocation saved in localStorage (e.g. Stockton) must win
      // over the account's default address (e.g. San Jose).
      const savedGuest = (() => {
        try { return JSON.parse(localStorage.getItem(GUEST_LOCATION_KEY)); } catch { return null; }
      })();
      const hasGuestLocation = savedGuest && savedGuest.lat != null && savedGuest.lng != null;

      // Single source of truth: users/{uid}. Consumers and business owners
      // both live here now (see migrateConsumerToUser above).
      const userDoc = await getDoc(doc(db, 'users', uid));
      let data = userDoc.exists() ? userDoc.data() : null;

      if (!data || !data.migratedFromConsumers) {
        data = await migrateConsumerToUser(uid, data);
      }

      if (data) {
        setUserData(data);
        setUserCollection('users');

        // Ensure Stripe customer exists
        await ensureStripeCustomer(uid, { ...data, email: data.email || auth.currentUser?.email });

        // Load favorites from Firestore and cache locally
        const favs = await loadAndCacheFavorites(db, uid);
        setFavorites(favs);

        // Load addresses subcollection from users/{uid}/addresses
        const userAddresses = await loadAddressesForUid(uid, 'users');
        setAddresses(userAddresses);

        if (!hasGuestLocation && userAddresses.length > 0) {
          const defaultAddress = userAddresses.find((a) => a.isDefault) || userAddresses[0];
          if (defaultAddress.lat && defaultAddress.lng) {
            setGuestLocation({
              lat: defaultAddress.lat,
              lng: defaultAddress.lng,
              address: defaultAddress.address,
              source: 'account',
            });
            setNeedsAddress(false);
            return;
          }
        }

        // Fallback to a root-level address field (legacy consumer docs kept
        // their default address directly on the profile instead of in the
        // addresses subcollection).
        if (!hasGuestLocation && data.lat && data.lng) {
          setGuestLocation({
            lat: data.lat,
            lng: data.lng,
            address: data.address,
            source: 'account',
          });
          setNeedsAddress(false);
          return;
        }

        if (hasGuestLocation) {
          setNeedsAddress(false);
          return;
        }

        // User exists but has no address -> prompt them
        setNeedsAddress(true);
        return;
      }

      // No user doc found anywhere
      setNeedsAddress(true);
      setUserData(null);
    } catch (e) {
      console.error('Error loading user data:', e);
      setNeedsAddress(true);
    } finally {
      setLoading(false);
    }
  };

  const reloadAddresses = async () => {
    if (!user) return;
    const list = await loadAddressesForUid(user.uid, userCollection);
    setAddresses(list);
  };

  const persistGuestLocation = (locationData) => {
    setGuestLocation(locationData);
    try {
      localStorage.setItem(GUEST_LOCATION_KEY, JSON.stringify(locationData));
    } catch (e) {
      console.error('Failed to save guest location:', e);
    }
  };

  const clearGuestLocation = () => {
    setGuestLocation(null);
    try {
      localStorage.removeItem(GUEST_LOCATION_KEY);
    } catch (e) {
      console.error('Failed to clear guest location:', e);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserData(null);
    setAddresses([]);
    setNeedsAddress(false);
    setFavorites([]);
    clearGuestLocation();
    clearCachedFavorites();
    // Tell HomePage not to auto-show the address modal after logout
    try {
      localStorage.setItem('ff_skip_address_prompt', '1');
    } catch (e) {
      // ignore
    }
  };

  const hasLocation = () => {
    return !!(guestLocation && guestLocation.lat && guestLocation.lng);
  };

  const addRemoveFavorite = async (storeId, storeData) => {
    if (!user) {
      // Guest: only localStorage
      const list = getCachedFavorites();
      const isFav = list.some((f) => f.storeId === storeId);
      if (isFav) {
        const next = list.filter((f) => f.storeId !== storeId);
        clearCachedFavorites();
        setCachedFavorites(next);
        setFavorites(next);
        return { isFavorite: false };
      }
      const item = {
        storeId,
        storeName: storeData.locationName || storeData.storeName || '',
        logoUrl: storeData.logoUrl || storeData.design?.logoUrl || '',
        address: storeData.address || storeData.city || '',
        latitude: storeData.latitude || null,
        longitude: storeData.longitude || null,
      };
      const next = [...list, item];
      clearCachedFavorites();
      setCachedFavorites(next);
      setFavorites(next);
      return { isFavorite: true };
    }
    // Logged in: Firestore + cache
    const result = await toggleFavorite(db, user.uid, storeId, storeData, favorites);
    setFavorites(result.list);
    return result;
  };

  const isFavorite = (storeId) => {
    return favorites.some((f) => f.storeId === storeId);
  };

  const reloadFavorites = async () => {
    if (!user) return;
    const list = await loadAndCacheFavorites(db, user.uid);
    setFavorites(list);
  };

  const switchAddress = (addr) => {
    if (!addr || !addr.lat || !addr.lng) return;
    setGuestLocation({
      lat: addr.lat,
      lng: addr.lng,
      address: addr.address,
      source: 'account',
    });
    try {
      localStorage.setItem(GUEST_LOCATION_KEY, JSON.stringify({
        lat: addr.lat,
        lng: addr.lng,
        address: addr.address,
        source: 'account',
      }));
    } catch (e) {
      console.error('Failed to save guest location:', e);
    }
  };

  const value = {
    user,
    userData,
    loading,
    auth,
    db,
    logout,
    guestLocation,
    setGuestLocation: persistGuestLocation,
    clearGuestLocation,
    hasLocation,
    needsAddress,
    setNeedsAddress,
    addresses,
    switchAddress,
    userCollection,
    reloadAddresses,
    favorites,
    addRemoveFavorite,
    isFavorite,
    reloadFavorites,
    showAddressModal,
    openAddressModal,
    closeAddressModal,
    showSignupModal,
    openSignupModal,
    closeSignupModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { app, auth, db };
